import { useMemo, useRef, useEffect, useCallback } from 'react';
import * as Yup from 'yup';
import { debounce } from 'debounce';

import { monthOptions } from '../company/components/periodSelector/date';
import { companyNameIncludedOptions } from '../company/companyNameIncludedOptions';

export const initialPeriodValues = {
  fromMonth: monthOptions()[0],
  fromYear: 2010,
  toMonth: monthOptions()[0],
  toYear: 2020,
};

// Helper function to parse domains from textarea input
const parseDomains = (value) => {
  if (!value || typeof value !== 'string') return [];

  return [value];
};

// Helper function to convert array back to textarea string
const domainsToString = (domains) => {
  if (!Array.isArray(domains)) return '';
  return domains.join('\n');
};

export const useCompanyDomainFilter = ({ setFilters, value, companyNameIncluded, companyNamePeriod }) => {
  const formikRef = useRef(null);

  const initialValues = useMemo(
    () => ({
      companyDomain: domainsToString(value) || '',
      companyNameIncluded: companyNameIncluded || companyNameIncludedOptions[0],
      companyNamePeriod: companyNamePeriod || initialPeriodValues,
    }),
    [value, companyNameIncluded, companyNamePeriod],
  );

  const formSchema = Yup.object().shape({
    companyDomain: Yup.string(),
    companyNameIncluded: Yup.object().nullable(),
    companyNamePeriod: Yup.object().nullable(),
  });

  // Keep the latest handler in a ref so it can be updated without recreating debounce
  const handleSubmitRef = useRef((formValues) => {
    // Parse the textarea value into an array of domains
    const domainArray = parseDomains(formValues.companyDomain);

    const filters = {};
    const removeFilters = [];

    if (domainArray.length > 0) {
      filters.companyDomain = domainArray;
      filters.companyNameIncluded = formValues.companyNameIncluded;

      if (formValues.companyNameIncluded?.value === 'specific_period') {
        filters.companyNamePeriod = formValues.companyNamePeriod;
      } else {
        // Remove companyNamePeriod when not in 'specific_period' mode
        removeFilters.push('companyNamePeriod');
      }
    } else {
      // When no domains are entered, remove both filters
      removeFilters.push('companyDomain', 'companyNameIncluded', 'companyNamePeriod');
    }

    setFilters(filters, removeFilters.length > 0 ? { removeFilters } : {});
  });

  // Update the ref when setFilters changes
  useEffect(() => {
    handleSubmitRef.current = (formValues) => {
      const domainArray = parseDomains(formValues.companyDomain);
      const filters = {};
      const removeFilters = [];

      if (domainArray.length > 0) {
        filters.companyDomain = domainArray;
        filters.companyNameIncluded = formValues.companyNameIncluded;

        if (formValues.companyNameIncluded?.value === 'specific_period') {
          filters.companyNamePeriod = formValues.companyNamePeriod;
        } else {
          removeFilters.push('companyNamePeriod');
        }
      } else {
        removeFilters.push('companyDomain', 'companyNameIncluded', 'companyNamePeriod');
      }

      setFilters(filters, removeFilters.length > 0 ? { removeFilters } : {});
    };
  }, [setFilters]);

  // Create debounced function ONCE - never recreate it
  const debouncedSubmit = useMemo(
    () =>
      debounce((formValues) => {
        handleSubmitRef.current(formValues);
      }, 600),
    [], // Empty deps - create once and never recreate
  );

  const onSubmit = useCallback(
    (formValues) => {
      debouncedSubmit(formValues);
    },
    [debouncedSubmit],
  );

  // Reset form when filters are cleared externally
  useEffect(() => {
    // If external value is empty but form still has content, filters were cleared
    if ((!value || value.length === 0) && formikRef.current) {
      const currentValues = formikRef.current.values;
      if (currentValues.companyDomain) {
        formikRef.current.resetForm({ values: initialValues });
      }
    }
  }, [value, initialValues]);

  return {
    formSchema,
    onSubmit,
    initialValues,
    formikRef,
  };
};
